import { useState, useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";

import Modal from "@mui/joy/Modal";
import ModalDialog from "@mui/joy/ModalDialog";
import ModalOverflow from "@mui/joy/ModalOverflow";
import Box from "@mui/joy/Box";
import Stack from "@mui/joy/Stack";
import Typography from "@mui/joy/Typography";
import Button from "@mui/joy/Button";
import IconButton from "@mui/joy/IconButton";
import Input from "@mui/joy/Input";
import Chip from "@mui/joy/Chip";
import CircularProgress from "@mui/joy/CircularProgress";
import Textarea from "@mui/joy/Textarea";

import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import CardGiftcardRoundedIcon from "@mui/icons-material/CardGiftcardRounded";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import PersonRoundedIcon from "@mui/icons-material/PersonRounded";
import PhoneRoundedIcon from "@mui/icons-material/PhoneRounded";
import EmailRoundedIcon from "@mui/icons-material/EmailRounded";
import CakeRoundedIcon from "@mui/icons-material/CakeRounded";
import MessageRoundedIcon from "@mui/icons-material/MessageRounded";

import { bookApi } from "../../api.js";


// ─── Поле формы (переиспользуемое) ──────────────────────────────────────────

function GiftField({ label, icon, required, error, ...inputProps }) {
  return (
    <Box>
      <Typography level="body-xs" sx={{
        mb: 0.5, fontWeight: 600,
        color: error ? "danger.500" : "text.secondary",
      }}>
        {label}
        {required && <Box component="span" sx={{ color: "danger.400", ml: 0.25 }}>*</Box>}
      </Typography>
      <Input
        startDecorator={icon}
        size="sm"
        error={!!error}
        sx={{ borderRadius: "10px" }}
        {...inputProps}
      />
      {error && (
        <Typography level="body-xs" sx={{ color: "danger.500", mt: 0.25 }}>
          {error}
        </Typography>
      )}
    </Box>
  );
}


// ─── Секционный заголовок ────────────────────────────────────────────────────

function SectionLabel({ children }) {
  return (
    <Typography level="body-xs" sx={{
      fontWeight: 700, textTransform: "uppercase",
      letterSpacing: "0.08em", color: "text.tertiary",
      mb: 1.25, fontSize: "10px",
    }}>
      {children}
    </Typography>
  );
}


// ─── Переливающийся градиент для кнопок ──────────────────────────────────────

const giftGradientSx = {
  background: "linear-gradient(135deg, #f472b6 0%, #fb923c 40%, #a78bfa 100%)",
  backgroundSize: "200% 200%",
  animation: "giftBtnShimmer 3s ease infinite",
  color: "white",
  "&:hover": {
    background: "linear-gradient(135deg, #ec4899 0%, #f97316 40%, #8b5cf6 100%)",
    transform: "scale(1.02)",
  },
  "@keyframes giftBtnShimmer": {
    "0%":   { backgroundPosition: "0% 50%" },
    "50%":  { backgroundPosition: "100% 50%" },
    "100%": { backgroundPosition: "0% 50%" },
  },
};


export default function ModalGift({ open, onClose }) {
  const [formats, setFormats] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedFormat, setSelectedFormat] = useState(null);
  const [selectedVariant, setSelectedVariant] = useState(null);

  // Шаги: "choose" → "details" → "result"
  const [step, setStep] = useState("choose");

  // Данные формы (шаг details)
  const [form, setForm] = useState({
    senderName: "",
    senderPhone: "",
    senderEmail: "",
    senderBirthDate: "",
    recipientName: "",
    recipientPhone: "",
    wishes: "",
  });
  const [errors, setErrors] = useState({});

  // Результат
  const [token, setToken] = useState("");
  const [copied, setCopied] = useState(false);
  const [creating, setCreating] = useState(false);

  // ── Сброс при открытии ─────────────────────────────────────────────────────
  useEffect(() => {
    if (!open) return;
    setStep("choose");
    setSelectedFormat(null);
    setSelectedVariant(null);
    setForm({
      senderName: "", senderPhone: "", senderEmail: "", senderBirthDate: "",
      recipientName: "", recipientPhone: "", wishes: "",
    });
    setErrors({});
    setToken("");
    setCopied(false);
    setLoading(true);
    bookApi.getBookFormats()
      .then(data => setFormats(data))
      .catch(err => console.error("Ошибка загрузки форматов:", err))
      .finally(() => setLoading(false));
  }, [open]);

  // ── Обновление поля формы ──────────────────────────────────────────────────
  const upd = (key, val) => {
    setForm(f => ({ ...f, [key]: val }));
    if (errors[key]) setErrors(e => ({ ...e, [key]: undefined }));
  };

  // ── Переход choose → details ───────────────────────────────────────────────
  const goToDetails = () => {
    if (!selectedFormat || !selectedVariant) return;
    setStep("details");
  };

  // ── Назад details → choose ─────────────────────────────────────────────────
  const goBackToChoose = () => {
    setStep("choose");
  };

  // ── Валидация формы ────────────────────────────────────────────────────────
  const validate = () => {
    const e = {};
    if (!form.senderName.trim())  e.senderName  = "Введите ваше имя";
    if (!form.senderPhone.trim()) e.senderPhone = "Введите ваш телефон";
    if (form.senderPhone && !/^[\d\s\+\-\(\)]{7,}$/.test(form.senderPhone))
      e.senderPhone = "Некорректный номер";
    if (form.senderEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.senderEmail))
      e.senderEmail = "Некорректный email";
    return e;
  };

  // ── Создание подарка ───────────────────────────────────────────────────────
  const handleCreate = async () => {
    const e = validate();
    if (Object.keys(e).length) { setErrors(e); return; }

    setCreating(true);
    try {
      const gift = await bookApi.createGiftAnonymous({
        bookFormatId:   selectedFormat.id,
        pagesCount:     selectedVariant.pages,
        senderName:     form.senderName.trim(),
        senderPhone:    form.senderPhone.trim(),
        senderEmail:    form.senderEmail.trim(),
        senderBirthDate: form.senderBirthDate || undefined,
        recipientName:  form.recipientName.trim(),
        recipientPhone: form.recipientPhone.trim(),
        wishes:         form.wishes.trim(),
      });
      setToken(gift.token);
      setStep("result");
    } catch (err) {
      alert("Ошибка создания подарка: " + err.message);
    } finally {
      setCreating(false);
    }
  };

  // ── Копирование токена ─────────────────────────────────────────────────────
  const handleCopy = () => {
    navigator.clipboard.writeText(token);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const gradientBg = {
    background: "linear-gradient(135deg, rgba(244,114,182,0.08) 0%, rgba(251,146,60,0.06) 40%, rgba(167,139,250,0.08) 100%)",
  };

  // ── Цена выбранного варианта ───────────────────────────────────────────────
  const selectedPrice = selectedVariant
    ? Number(selectedVariant.price).toLocaleString("ru-RU") + " ₽"
    : null;

  return (
    <AnimatePresence>
      {open && (
        <Modal
          open={open}
          onClose={onClose}
          sx={{ zIndex: 10013 }}
          slotProps={{
            backdrop: {
              component: motion.div,
              initial: { opacity: 0, backdropFilter: "blur(0px)" },
              animate: { opacity: 1, backdropFilter: "blur(8px)" },
              exit: { opacity: 0, backdropFilter: "blur(0px)" },
              transition: { duration: 0.3, ease: "easeInOut" },
            },
          }}
        >
          <ModalOverflow sx={{ height: "auto", p: 0 }}>
            <ModalDialog
              component={motion.div}
              initial={{ opacity: 0, y: -10, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 10, scale: 0.98 }}
              transition={{ duration: 0.2, ease: "easeInOut" }}
              sx={(theme) => ({
                p: 0, overflow: "hidden",
                ...gradientBg,
                // border: "1.5px solid", borderColor: "rgba(244,114,182,0.2)",
                border: "none",
                bgcolor: 'white',
                [theme.breakpoints.up("sm")]: {
                  width: 500, maxHeight: "90vh", borderRadius: "22px",
                },
                [theme.breakpoints.down("sm")]: {
                  width: "100vw", minWidth: "100vw",
                  minHeight: "100vh", m: 0, borderRadius: 0,
                },
              })}
            >
              {/* Закрыть */}
              <IconButton
                size="sm" variant="plain" color="neutral"
                onClick={onClose}
                sx={{
                  position: "absolute", top: 12, right: 12, zIndex: 10,
                  borderRadius: "10px", bgcolor: "rgba(0,0,0,0.05)",
                  "&:hover": { bgcolor: "rgba(0,0,0,0.10)" },
                }}
              >
                <CloseRoundedIcon sx={{ fontSize: 20 }} />
              </IconButton>

              {/* ── Переливающийся баннер ── */}
              <Box sx={{
                px: 3, pt: 3, pb: 2, textAlign: "center",
                background: "linear-gradient(135deg, #f472b6 0%, #fb923c 40%, #a78bfa 100%)",
                backgroundSize: "200% 200%",
                animation: "giftBannerShimmer 4s ease infinite",
                "@keyframes giftBannerShimmer": {
                  "0%":   { backgroundPosition: "0% 50%" },
                  "50%":  { backgroundPosition: "100% 50%" },
                  "100%": { backgroundPosition: "0% 50%" },
                },
              }}>
                <CardGiftcardRoundedIcon sx={{ fontSize: 40, color: "white", mb: 1 }} />
                <Typography level="h4" sx={{ color: "white", fontWeight: 800, letterSpacing: "-0.02em" }}>
                  {step === "details" ? "Данные для подарка" : "Фотокнига в подарок"}
                </Typography>
                <Typography level="body-sm" sx={{ color: "rgba(255,255,255,0.85)", mt: 0.5 }}>
                  {step === "choose" && "Подарите близким уникальную фотокнигу"}
                  {step === "details" && "Заполните информацию о себе и получателе"}
                  {step === "result" && "Подарок готов!"}
                </Typography>
              </Box>

              {/* ── Контент ── */}
              <Box sx={{ px: 3, py: 2.5, overflowY: "auto" }}>
                <AnimatePresence mode="wait">

                  {/* ═══════════ ШАГ 1: ВЫБОР ФОРМАТА ═══════════ */}
                  {step === "choose" && (
                    <motion.div
                      key="choose"
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: 20 }}
                      transition={{ duration: 0.2 }}
                    >
                      {loading ? (
                        <Stack alignItems="center" py={4}>
                          <CircularProgress size="md" />
                          <Typography level="body-sm" color="neutral" sx={{ mt: 1 }}>
                            Загружаем форматы…
                          </Typography>
                        </Stack>
                      ) : (
                        <>
                          <SectionLabel>Формат книги</SectionLabel>
                          <Stack gap={1} mb={2}>
                            {formats.map(fmt => (
                              <Box
                                key={fmt.id}
                                onClick={() => { setSelectedFormat(fmt); setSelectedVariant(null); }}
                                sx={{
                                  p: 1.5, borderRadius: "12px",
                                  border: "2px solid",
                                  borderColor: selectedFormat?.id === fmt.id
                                    ? "rgba(244,114,182,0.6)" : "divider",
                                  bgcolor: selectedFormat?.id === fmt.id
                                    ? "rgba(244,114,182,0.06)" : "background.surface",
                                  cursor: "pointer",
                                  transition: "all 0.15s",
                                  "&:hover": { borderColor: "rgba(244,114,182,0.4)" },
                                }}
                              >
                                <Typography level="body-sm" fontWeight={600}>
                                  {fmt.title}
                                </Typography>
                                <Typography level="body-xs" color="neutral">
                                  {fmt.width_mm}×{fmt.height_mm}мм
                                </Typography>
                              </Box>
                            ))}
                          </Stack>

                          {selectedFormat?.page_variants?.length > 0 && (
                            <>
                              <SectionLabel>Количество страниц</SectionLabel>
                              <Stack direction="row" gap={1} flexWrap="wrap" mb={2.5}>
                                {selectedFormat.page_variants.map(v => (
                                  <Chip
                                    key={v.id}
                                    size="lg"
                                    variant={selectedVariant?.id === v.id ? "solid" : "outlined"}
                                    onClick={() => setSelectedVariant(v)}
                                    sx={{
                                      cursor: "pointer", fontWeight: 600, borderRadius: "10px",
                                      ...(selectedVariant?.id === v.id ? {
                                        background: "linear-gradient(135deg, #f472b6, #a78bfa)",
                                        color: "white",
                                      } : {}),
                                    }}
                                  >
                                    {v.pages} стр. — {Number(v.price).toLocaleString("ru-RU")} ₽
                                  </Chip>
                                ))}
                              </Stack>
                            </>
                          )}

                          <Button
                            fullWidth size="lg"
                            disabled={!selectedFormat || !selectedVariant}
                            onClick={goToDetails}
                            sx={{ borderRadius: "12px", fontWeight: 700, ...giftGradientSx }}
                          >
                            Продолжить
                          </Button>
                        </>
                      )}
                    </motion.div>
                  )}


                  {/* ═══════════ ШАГ 2: ДАННЫЕ ═══════════ */}
                  {step === "details" && (
                    <motion.div
                      key="details"
                      initial={{ opacity: 0, x: 40 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -40 }}
                      transition={{ duration: 0.22 }}
                    >
                      {/* Кнопка назад */}
                      <Button
                        size="sm" variant="plain" color="neutral"
                        startDecorator={<ArrowBackRoundedIcon sx={{ fontSize: 16 }} />}
                        onClick={goBackToChoose}
                        sx={{ mb: 2, borderRadius: "10px", fontWeight: 600, fontSize: "13px", px: 1 }}
                      >
                        Назад к выбору формата
                      </Button>

                      {/* Сводка выбранного */}
                      <Box sx={{
                        p: 1.5, mb: 2.5, borderRadius: "12px",
                        border: "1.5px solid rgba(244,114,182,0.25)",
                        bgcolor: "rgba(244,114,182,0.04)",
                        display: "flex", alignItems: "center", justifyContent: "space-between",
                      }}>
                        <Box>
                          <Typography level="body-sm" fontWeight={700}>
                            {selectedFormat?.title}
                          </Typography>
                          <Typography level="body-xs" color="neutral">
                            {selectedVariant?.pages} стр. · {selectedFormat?.width_mm}×{selectedFormat?.height_mm}мм
                          </Typography>
                        </Box>
                        <Chip size="sm" variant="soft" sx={{
                          fontWeight: 700,
                          background: "linear-gradient(135deg, rgba(244,114,182,0.15), rgba(167,139,250,0.15))",
                          color: "#d946ef",
                        }}>
                          {selectedPrice}
                        </Chip>
                      </Box>

                      {/* ── Данные дарителя (обязательные) ── */}
                      <SectionLabel>Ваши данные (даритель)</SectionLabel>
                      <Stack gap={1.25} mb={2.5}>
                        <GiftField
                          label="Имя и фамилия"
                          required
                          icon={<PersonRoundedIcon sx={{ fontSize: 16, color: "text.tertiary" }} />}
                          placeholder="Иванов Иван"
                          value={form.senderName}
                          onChange={e => upd("senderName", e.target.value)}
                          error={errors.senderName}
                        />
                        <GiftField
                          label="Телефон"
                          required
                          icon={<PhoneRoundedIcon sx={{ fontSize: 16, color: "text.tertiary" }} />}
                          placeholder="+7 900 000-00-00"
                          value={form.senderPhone}
                          onChange={e => upd("senderPhone", e.target.value)}
                          error={errors.senderPhone}
                        />
                        <GiftField
                          label="Email"
                          icon={<EmailRoundedIcon sx={{ fontSize: 16, color: "text.tertiary" }} />}
                          placeholder="mail@example.com"
                          value={form.senderEmail}
                          onChange={e => upd("senderEmail", e.target.value)}
                          error={errors.senderEmail}
                        />
                        <GiftField
                          label="Дата рождения"
                          icon={<CakeRoundedIcon sx={{ fontSize: 16, color: "text.tertiary" }} />}
                          type="date"
                          value={form.senderBirthDate}
                          onChange={e => upd("senderBirthDate", e.target.value)}
                          slotProps={{ input: { style: { fontSize: 13 } } }}
                        />
                      </Stack>

                      {/* ── Данные получателя (необязательные) ── */}
                      <SectionLabel>Данные получателя (необязательно)</SectionLabel>
                      <Stack gap={1.25} mb={2.5}>
                        <GiftField
                          label="Имя получателя"
                          icon={<PersonRoundedIcon sx={{ fontSize: 16, color: "text.tertiary" }} />}
                          placeholder="Кому вы дарите?"
                          value={form.recipientName}
                          onChange={e => upd("recipientName", e.target.value)}
                        />
                        <GiftField
                          label="Телефон получателя"
                          icon={<PhoneRoundedIcon sx={{ fontSize: 16, color: "text.tertiary" }} />}
                          placeholder="+7 900 000-00-00"
                          value={form.recipientPhone}
                          onChange={e => upd("recipientPhone", e.target.value)}
                        />

                        {/* Пожелания — textarea */}
                        <Box>
                          <Typography level="body-xs" sx={{ mb: 0.5, fontWeight: 600, color: "text.secondary" }}>
                            Пожелания
                          </Typography>
                          <Textarea
                            size="sm"
                            minRows={2}
                            maxRows={4}
                            placeholder="С днём рождения! Наполни эту книгу лучшими моментами 💛"
                            value={form.wishes}
                            onChange={e => upd("wishes", e.target.value)}
                            startDecorator={
                              <MessageRoundedIcon sx={{ fontSize: 16, color: "text.tertiary", mt: 0.5, ml: 0.25 }} />
                            }
                            sx={{ borderRadius: "10px" }}
                          />
                        </Box>
                      </Stack>

                      {/* Кнопка создать */}
                      <Button
                        fullWidth size="lg"
                        loading={creating}
                        onClick={handleCreate}
                        startDecorator={<CardGiftcardRoundedIcon />}
                        sx={{ borderRadius: "12px", fontWeight: 700, ...giftGradientSx }}
                      >
                        Оформить подарок — {selectedPrice}
                      </Button>
                    </motion.div>
                  )}


                  {/* ═══════════ ШАГ 3: РЕЗУЛЬТАТ ═══════════ */}
                  {step === "result" && (
                    <motion.div
                      key="result"
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.3 }}
                    >
                      <Stack alignItems="center" gap={2} py={2}>
                        <motion.div
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          transition={{ type: "spring", stiffness: 400, damping: 22 }}
                        >
                          <Box sx={{
                            width: 72, height: 72, borderRadius: "50%",
                            background: "linear-gradient(135deg, rgba(244,114,182,0.15), rgba(167,139,250,0.15))",
                            display: "flex", alignItems: "center", justifyContent: "center",
                          }}>
                            <CardGiftcardRoundedIcon sx={{ fontSize: 36, color: "#f472b6" }} />
                          </Box>
                        </motion.div>

                        <Typography level="title-lg" fontWeight={800} textAlign="center">
                          Подарок готов! 🎉
                        </Typography>

                        <Typography level="body-sm" color="neutral" textAlign="center" sx={{ maxWidth: 340 }}>
                          Передайте этот код получателю. Его нужно ввести в поле «Код подарка» при оформлении заказа.
                        </Typography>

                        {/* Токен */}
                        <Box sx={{
                          display: "flex", alignItems: "center", gap: 1.5,
                          px: 2.5, py: 1.5, borderRadius: "14px",
                          border: "2px dashed rgba(244,114,182,0.4)",
                          bgcolor: "rgba(244,114,182,0.06)",
                        }}>
                          <Typography level="h3" sx={{
                            fontWeight: 900, letterSpacing: "0.15em", fontFamily: "monospace",
                            background: "linear-gradient(135deg, #f472b6, #a78bfa)",
                            WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
                          }}>
                            {token}
                          </Typography>
                          <IconButton
                            size="sm" variant="soft"
                            onClick={handleCopy}
                            sx={{
                              borderRadius: "8px",
                              bgcolor: copied ? "success.100" : "rgba(244,114,182,0.12)",
                              color: copied ? "success.600" : "#f472b6",
                            }}
                          >
                            {copied
                              ? <CheckRoundedIcon sx={{ fontSize: 16 }} />
                              : <ContentCopyRoundedIcon sx={{ fontSize: 16 }} />}
                          </IconButton>
                        </Box>

                        {/* Инструкция */}
                        <Box sx={{ bgcolor: "background.level1", borderRadius: "12px", p: 2, width: "100%" }}>
                          <Typography level="body-xs" fontWeight={700} sx={{ mb: 1, color: "text.secondary" }}>
                            Как активировать:
                          </Typography>
                          <Stack gap={0.75}>
                            {[
                              "Передайте код получателю",
                              "Получатель создаёт свою фотокнигу",
                              "При оформлении заказа вводит код в поле «Код подарка»",
                              "Нажимает «Активировать» — готово!",
                            ].map((text, i) => (
                              <Stack key={i} direction="row" gap={1} alignItems="flex-start">
                                <Box sx={{
                                  width: 18, height: 18, borderRadius: "50%",
                                  background: "linear-gradient(135deg, #f472b6, #a78bfa)",
                                  display: "flex", alignItems: "center", justifyContent: "center",
                                  flexShrink: 0, mt: 0.125,
                                }}>
                                  <Typography sx={{ fontSize: "10px", color: "white", fontWeight: 800 }}>
                                    {i + 1}
                                  </Typography>
                                </Box>
                                <Typography level="body-xs">{text}</Typography>
                              </Stack>
                            ))}
                          </Stack>
                        </Box>

                        <Button
                          fullWidth variant="outlined" color="neutral"
                          onClick={onClose}
                          sx={{ borderRadius: "12px", fontWeight: 600, mt: 1 }}
                        >
                          Закрыть
                        </Button>
                      </Stack>
                    </motion.div>
                  )}

                </AnimatePresence>
              </Box>
            </ModalDialog>
          </ModalOverflow>
        </Modal>
      )}
    </AnimatePresence>
  );
}